import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import { getEmailSettings, setEmailSetting } from "../services/emailSettingsApi";
import { fetchCompanyloged } from "../services/apiService";
import { getUserFromToken } from "../services/authService";

const PRIMARY = "rgb(18, 76, 114)";

// Must match Hiring_Api NotificationTypes keys.
const NOTIFICATION_TYPES = [
  { key: "ApprovalWorkflow", label: "Approval Workflow (Finance / HR / CEO)" },
  { key: "Submission", label: "Submission to HR" },
  { key: "HiringManagerApproval", label: "Hiring Manager Approval Update" },
  { key: "CandidateStatus", label: "Candidate Status Update" },
  { key: "DirectEvaluation", label: "Direct Evaluation Request" },
  { key: "CandidateFinalStatus", label: "Candidate Final Status" },
  { key: "Sourcing", label: "Sourcing Assignment" },
  { key: "Interview", label: "Interview Invitation" },
];

// Small reusable toggle switch.
const Toggle = ({ checked, disabled, onChange }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onChange(!checked)}
    className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50"
    style={{ backgroundColor: checked ? "#059669" : "#d1d5db" }}
    aria-pressed={checked}
  >
    <span
      className="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200"
      style={{ transform: checked ? "translateX(22px)" : "translateX(2px)", marginTop: "2px" }}
    />
  </button>
);

export default function EmailSettingsManager() {
  const [settings, setSettings] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const modifiedBy = getUserFromToken();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [rows, comps] = await Promise.all([getEmailSettings(), fetchCompanyloged()]);
      setSettings(Array.isArray(rows) ? rows : []);
      setCompanies(Array.isArray(comps) ? comps : []);
    } catch (err) {
      console.error("Failed to load email settings", err);
      toast.error("Failed to load email settings.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Enabled unless an explicit off-row exists for this exact (company, type) scope.
  const isEnabled = (companyCode, notificationType) => {
    const row = settings.find(
      (s) => (s.company_code ?? null) === (companyCode ?? null) &&
             (s.notification_type ?? null) === (notificationType ?? null)
    );
    if (!row) return true; // default ON
    return row.is_enabled === true || row.is_enabled === 1;
  };

  const keyOf = (companyCode, notificationType) => `${companyCode ?? "*"}|${notificationType ?? "*"}`;

  const handleToggle = async (companyCode, notificationType, nextEnabled) => {
    const k = keyOf(companyCode, notificationType);
    setSavingKey(k);
    try {
      await setEmailSetting({ companyCode, notificationType, isEnabled: nextEnabled, modifiedBy });
      // Update local state (upsert)
      setSettings((prev) => {
        const idx = prev.findIndex(
          (s) => (s.company_code ?? null) === (companyCode ?? null) &&
                 (s.notification_type ?? null) === (notificationType ?? null)
        );
        const updated = { company_code: companyCode ?? null, notification_type: notificationType ?? null, is_enabled: nextEnabled };
        if (idx === -1) return [...prev, updated];
        const copy = [...prev];
        copy[idx] = { ...copy[idx], is_enabled: nextEnabled };
        return copy;
      });
      toast.success(`Notifications ${nextEnabled ? "enabled" : "disabled"}.`);
    } catch (err) {
      toast.error("Failed to update setting.");
    } finally {
      setSavingKey(null);
    }
  };

  const globalOn = isEnabled(null, null);

  const Row = ({ label, companyCode, notificationType, sub }) => {
    const enabled = isEnabled(companyCode, notificationType);
    const k = keyOf(companyCode, notificationType);
    return (
      <div className="flex items-center justify-between py-3 px-4 border-b" style={{ borderColor: "rgba(18,76,114,0.08)" }}>
        <div>
          <div className="text-sm font-medium text-gray-800">{label}</div>
          {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs" style={{ color: enabled ? "#059669" : "#9ca3af" }}>
            {enabled ? "On" : "Off"}
          </span>
          <Toggle checked={enabled} disabled={savingKey === k} onChange={(v) => handleToggle(companyCode, notificationType, v)} />
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-8">
        <div className="w-5 h-5 rounded-full border-2 animate-spin" style={{ borderColor: "rgba(18,76,114,0.15)", borderTopColor: PRIMARY }} />
        <span className="text-sm" style={{ color: "rgba(18,76,114,0.6)" }}>Loading email settings...</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col 2xl:px-[10%] pt-8 bg-white md:pt-8">
      <div className="bg-white rounded-2xl p-6 filter drop-shadow-[0_5px_30px_rgba(10,60,96,0.10)]">
        <h3 className="text-xl sm:text-2xl text-primary-800 font-semibold">Email Notifications</h3>
        <p className="text-sm text-gray-500 mt-1">
          Control which notification emails the system sends. A switch that is Off blocks those emails immediately.
        </p>

        {/* Global master */}
        <div className="mt-6 rounded-xl border" style={{ borderColor: "rgba(18,76,114,0.15)" }}>
          <div className="px-4 py-3 rounded-t-xl" style={{ backgroundColor: "rgba(18,76,114,0.06)" }}>
            <span className="text-sm font-semibold" style={{ color: PRIMARY }}>Global</span>
          </div>
          <Row label="All email notifications" sub="Master switch — turning this Off stops every notification email system-wide." companyCode={null} notificationType={null} />
        </div>

        {/* Per notification type */}
        <div className="mt-6 rounded-xl border" style={{ borderColor: "rgba(18,76,114,0.15)", opacity: globalOn ? 1 : 0.5 }}>
          <div className="px-4 py-3 rounded-t-xl" style={{ backgroundColor: "rgba(18,76,114,0.06)" }}>
            <span className="text-sm font-semibold" style={{ color: PRIMARY }}>By Notification Type</span>
            {!globalOn && <span className="text-xs text-gray-500 ml-2">(overridden by Global = Off)</span>}
          </div>
          {NOTIFICATION_TYPES.map((t) => (
            <Row key={t.key} label={t.label} companyCode={null} notificationType={t.key} />
          ))}
        </div>

        {/* Per company */}
        {companies.length > 0 && (
          <div className="mt-6 rounded-xl border" style={{ borderColor: "rgba(18,76,114,0.15)", opacity: globalOn ? 1 : 0.5 }}>
            <div className="px-4 py-3 rounded-t-xl" style={{ backgroundColor: "rgba(18,76,114,0.06)" }}>
              <span className="text-sm font-semibold" style={{ color: PRIMARY }}>By Company</span>
            </div>
            {companies.map((c) => (
              <Row key={c.companyCode} label={c.companyName || c.companyCode} sub={c.companyCode} companyCode={c.companyCode} notificationType={null} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
